import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { API } from "@/utils/api";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Loader2, Plus, Download, Eye, Truck, Package,
} from "lucide-react";

const SOURCE_BADGE = {
  OWN: "bg-emerald-100 text-emerald-700 border-emerald-200",
  DELHIVERY_API: "bg-blue-100 text-blue-700 border-blue-200",
  MANUAL: "bg-slate-100 text-slate-600 border-slate-200",
  SYSTEM: "bg-purple-100 text-purple-700 border-purple-200",
  API: "bg-blue-100 text-blue-700 border-blue-200",
};

const EVENT_TYPES = [
  "ORDER_CREATED", "MANIFEST_CREATED", "PICKUP_SCHEDULED",
  "PICKUP_ATTEMPTED", "PICKED_UP", "FM_IN_TRANSIT",
  "ARRIVED_ORIGIN_POINT", "LOADED_TO_VEHICLE", "VEHICLE_DEPARTED",
  "IN_TRANSIT", "ARRIVED_DEST_POINT",
  "RECEIVED_BY_LM_PARTNER", "OUT_FOR_DELIVERY", "DELIVERY_ATTEMPTED",
  "DELIVERED", "POD_UPLOADED",
  "NDR_RAISED", "ADDRESS_ISSUE", "CUSTOMER_NOT_AVAILABLE", "DAMAGED",
  "LOST", "UNEXPECTED_SCAN", "DELAYED", "RTO_INITIATED",
  "3PL_MANIFESTED", "3PL_IN_TRANSIT", "3PL_OUT_FOR_DELIVERY",
  "3PL_DELIVERED", "3PL_NDR",
];
const MIDMILE = new Set(["VEHICLE_DEPARTED", "LOADED_TO_VEHICLE", "IN_TRANSIT", "ARRIVED_DEST_POINT", "ARRIVED_ORIGIN_POINT"]);

const fmtType = (t) =>
  (t || "")
    .replace(/^3PL_/, "Courier ")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export default function ShipmentTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [liveStatus, setLiveStatus] = useState(null);

  // Manual scan form
  const [form, setForm] = useState({
    event_type: "PICKED_UP",
    event_date: "",
    display_message: "",
    internal_message: "",
    display_location: "",
    vehicle_number: "",
    attempt_number: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const sh = await api.get(`/shipments/${id}`).catch(() => ({ data: null }));
      setShipment(sh.data);
      const awb = sh.data?.tracking_id || sh.data?.awb || sh.data?.awb_number;
      if (!awb) {
        toast.error("Shipment has no AWB");
        return;
      }
      const r = await api.get(`/v2/tracking/${awb}/full`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tracking");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const awb = data?.awb || shipment?.tracking_id;
  const lr = data?.lr_number || shipment?.lr_number || shipment?.order_id;
  const is3pl = ["DELHIVERY_B2B", "DELHIVERY_B2C"].includes(
    (shipment?.network_type || "").toUpperCase()
  );

  const events = data?.events || [];
  const lastSync = [...events].reverse().find((e) => e.source === "DELHIVERY_API")?.event_date;

  const submitEvent = async () => {
    if (!form.event_type) return;
    setSubmitting(true);
    try {
      const payload = {
        event_type: form.event_type,
        display_message: form.display_message || undefined,
        internal_message: form.internal_message || undefined,
        display_location: form.display_location || undefined,
        vehicle_number: form.vehicle_number || undefined,
        attempt_number: form.attempt_number ? Number(form.attempt_number) : undefined,
        event_date: form.event_date || undefined,
      };
      await api.post(`/v2/tracking/${awb}/event`, payload);
      toast.success("Event added");
      setForm({ ...form, display_message: "", internal_message: "", display_location: "", vehicle_number: "", attempt_number: "" });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const sync3PL = async () => {
    if (!lr) return;
    setSyncing(true);
    try {
      const r = await api.post(`/v2/tracking/sync-3pl/${lr}`);
      const d = r.data;
      toast.success(`Synced ${d.synced_count} new events (${d.skipped_count} skipped)`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const checkLive = async () => {
    try {
      const r = await api.get(`/v2/tracking/3pl/status/${awb}`);
      setLiveStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const downloadLabel = async (singleAwb) => {
    try {
      const token = localStorage.getItem("sendtoo_token");
      const url = singleAwb ? `${API}/v2/labels/${id}/${singleAwb}` : `${API}/v2/labels/${id}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `Sendtoo_Label_${singleAwb || lr || id}.pdf`;
      a.click();
      URL.revokeObjectURL(u);
    } catch {
      toast.error("Label download failed");
    }
  };

  const awbs = shipment?.awb_numbers || shipment?.awbs || (awb ? [awb] : []);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="shipment-tracking-page">
        <button
          onClick={() => navigate("/admin/shipments")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
          data-testid="back-to-shipments"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Shipments
        </button>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Tracking — {awb || "—"}</h1>
            <p className="text-sm text-slate-500">LR: {lr || "—"}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadAll} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs font-bold text-slate-500 uppercase mb-2">Pickup Location</div>
            <div className="font-semibold text-slate-800">{shipment?.origin?.name}</div>
            <div className="text-sm text-slate-600 mt-1">{shipment?.origin?.address}</div>
            <div className="text-sm text-slate-600">
              {shipment?.origin?.city}, {shipment?.origin?.state} — {shipment?.origin?.pincode}
            </div>
            <div className="text-sm text-slate-500 mt-1">{shipment?.origin?.phone}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs font-bold text-slate-500 uppercase mb-2">Drop Location</div>
            <div className="font-semibold text-slate-800">{shipment?.destination?.name}</div>
            <div className="text-sm text-slate-600 mt-1">{shipment?.destination?.address}</div>
            <div className="text-sm text-slate-600">
              {shipment?.destination?.city}, {shipment?.destination?.state} — {shipment?.destination?.pincode}
            </div>
            <div className="text-sm text-slate-500 mt-1">{shipment?.destination?.phone}</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs font-bold text-slate-500 uppercase mb-3">Package Details</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-slate-400 text-xs">Weight (Declared)</div>
              <div className="font-semibold">{shipment?.package?.weight || shipment?.chargeable_weight} kg</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">Number of Boxes</div>
              <div className="font-semibold">{shipment?.box_count || 1}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">LR Number</div>
              <div className="font-semibold">{lr || "—"}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">Amount (Original)</div>
              <div className="font-semibold">₹{shipment?.price}</div>
            </div>
          </div>
          <div className="mt-4">
            <div className="text-slate-400 text-xs mb-2">
              {shipment?.box_count > 1 ? "AWB Numbers (per box)" : "AWB"}
            </div>
            {shipment?.is_multibox && Array.isArray(shipment?.awb_numbers) && shipment.awb_numbers.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {shipment.awb_numbers.map((a, i) => (
                  <span key={i} className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                    Box {i + 1}: {a}
                  </span>
                ))}
              </div>
            ) : (
              <span className="font-mono text-sm font-semibold">{awb || "—"}</span>
            )}
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="text-xs font-bold text-amber-700 uppercase mb-2">Weight & Amount Reconciliation</div>
          {shipment?.v2_actual_weight || shipment?.weight_mismatch ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-slate-500 text-xs">Corrected Weight</div>
                <div className="font-semibold">{shipment.v2_actual_weight} kg</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">Corrected Amount</div>
                <div className="font-semibold">₹{shipment.v2_corrected_price || "Pending"}</div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-amber-700">Not yet reconciled by carrier. This section will update once weight verification is complete.</p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
          {/* LEFT — Timeline */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">
              Full Timeline ({events.length} events)
            </h3>
            {loading ? (
              <div className="py-12 flex justify-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : events.length === 0 ? (
              <div className="py-12 text-center text-slate-400">
                <Package className="w-10 h-10 mx-auto mb-2 opacity-60" />
                <p className="text-sm">No events yet</p>
              </div>
            ) : (
              <div className="relative">
                <div className="absolute left-3 top-2 bottom-2 w-px bg-slate-200" />
                <div className="space-y-4">
                  {[...events]
                    .sort((a, b) => new Date(b.event_date || 0) - new Date(a.event_date || 0))
                    .map((ev, i) => (
                      <div key={ev.event_id || i} className="relative pl-9" data-testid={`event-${ev.event_type}`}>
                        <div
                          className={`absolute left-1 top-1.5 w-5 h-5 rounded-full border-2 ${
                            i === 0 ? "bg-slate-800 border-slate-800 animate-pulse" : "bg-emerald-500 border-emerald-500"
                          }`}
                        />
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="font-semibold text-slate-800 text-sm">{fmtType(ev.event_type)}</div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${SOURCE_BADGE[ev.source] || SOURCE_BADGE.MANUAL}`}>
                            {ev.source}
                          </span>
                        </div>
                        <div className="text-sm text-slate-600 mt-0.5">{ev.display_message}</div>
                        {ev.internal_message && ev.internal_message !== ev.display_message && (
                          <div className="text-xs text-slate-500 italic mt-0.5">
                            <span className="font-semibold not-italic">Internal: </span>
                            {ev.internal_message}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500 mt-1.5">
                          {ev.display_location && <span>📍 {ev.display_location}</span>}
                          {ev.vehicle_number && <span>🚚 {ev.vehicle_number}</span>}
                          {ev.attempt_number != null && <span>Attempt #{ev.attempt_number}</span>}
                          {ev.scanned_by && (
                            <span>
                              By {ev.scanned_by_type || "—"}
                            </span>
                          )}
                          <span>{ev.event_date ? new Date(ev.event_date).toLocaleString("en-IN") : "—"}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — Action panel */}
          <div className="space-y-4">
            <EwayBillSection shipment={shipment} onUpdated={loadAll} />

            {/* Manual scan */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Add Manual Scan</h3>
              <Field label="Event Type">
                <select
                  value={form.event_type}
                  onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                  className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white"
                  data-testid="event-type-select"
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{fmtType(t)} ({t})</option>
                  ))}
                </select>
              </Field>
              <Field label="Event Date">
                <Input
                  type="datetime-local"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  data-testid="event-date"
                />
              </Field>
              <Field label="Display Message">
                <Input
                  value={form.display_message}
                  onChange={(e) => setForm({ ...form, display_message: e.target.value })}
                  placeholder="(uses default if blank)"
                  data-testid="display-message"
                />
              </Field>
              <Field label="Internal Message">
                <Textarea
                  rows={2}
                  value={form.internal_message}
                  onChange={(e) => setForm({ ...form, internal_message: e.target.value })}
                  data-testid="internal-message"
                />
              </Field>
              <Field label="Display Location">
                <Input
                  value={form.display_location}
                  onChange={(e) => setForm({ ...form, display_location: e.target.value })}
                  placeholder="e.g. Hyderabad Hub"
                  data-testid="display-location"
                />
              </Field>
              {MIDMILE.has(form.event_type) && (
                <Field label="Vehicle Number">
                  <Input
                    value={form.vehicle_number}
                    onChange={(e) => setForm({ ...form, vehicle_number: e.target.value.toUpperCase() })}
                    placeholder="AP31AB1234"
                    data-testid="vehicle-number"
                  />
                </Field>
              )}
              {form.event_type === "DELIVERY_ATTEMPTED" && (
                <Field label="Attempt Number">
                  <Input
                    type="number"
                    min="1"
                    value={form.attempt_number}
                    onChange={(e) => setForm({ ...form, attempt_number: e.target.value })}
                    data-testid="attempt-number"
                  />
                </Field>
              )}
              <Button
                onClick={submitEvent}
                disabled={submitting}
                className="w-full bg-slate-800 hover:bg-slate-900 text-white"
                data-testid="submit-event"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Event
              </Button>
            </div>

            {/* 3PL Sync */}
            {is3pl && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Truck className="w-4 h-4" /> 3PL Sync
                </h3>
                <p className="text-xs text-slate-500">
                  Last synced: {lastSync ? new Date(lastSync).toLocaleString("en-IN") : "Never"}
                </p>
                <Button onClick={sync3PL} disabled={syncing} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="sync-3pl-btn">
                  {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Sync Now
                </Button>
                <Button onClick={checkLive} variant="outline" className="w-full" data-testid="check-live-btn">
                  <Eye className="w-4 h-4 mr-2" /> Check Live Status
                </Button>
              </div>
            )}

            {/* Labels */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Labels</h3>
              <Button onClick={() => downloadLabel(null)} variant="outline" className="w-full" data-testid="download-label-btn">
                <Download className="w-4 h-4 mr-2" /> Download Label{awbs.length > 1 ? "s (merged)" : ""}
              </Button>
              {awbs.length > 1 && awbs.map((a) => (
                <Button key={a} onClick={() => downloadLabel(a)} variant="ghost" className="w-full justify-start text-xs" data-testid={`download-label-${a}`}>
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Label for {a}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {liveStatus && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setLiveStatus(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-5 w-full max-w-lg shadow-2xl max-h-[80vh] overflow-auto" data-testid="live-status-modal">
            <h3 className="text-lg font-bold text-slate-800 mb-3">Live Delhivery Status</h3>
            <pre className="bg-slate-50 border border-slate-200 rounded p-3 text-xs overflow-auto">{JSON.stringify(liveStatus, null, 2)}</pre>
            <div className="flex justify-end mt-3">
              <Button onClick={() => setLiveStatus(null)} variant="outline">Close</Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">{label}</label>
      {children}
    </div>
  );
}


function EwayBillSection({ shipment, onUpdated }) {
  const [info, setInfo] = useState(null);
  const [val, setVal] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const sid = shipment?.id || shipment?._id;

  const load = useCallback(async () => {
    if (!sid) return;
    try {
      const r = await api.get(`/v2/shipments/${sid}/eway-bill`);
      setInfo(r.data);
    } catch { /* not available */ }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  if (!info) return null;
  if (!info.eway_bill_required) return null;

  const save = async () => {
    if (!/^\d{12}$/.test((val || "").trim())) {
      toast.error("Eway Bill must be exactly 12 digits");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/v2/shipments/${sid}/eway-bill`, { eway_bill_number: val.trim() });
      toast.success("Eway Bill verified ✓");
      setEditing(false);
      setVal("");
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const masked = info.eway_bill_number
    ? `XXXX-XXXX-${info.eway_bill_number.slice(-4)}`
    : "";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="eway-bill-section">
      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Eway Bill</h3>

      {!info.eway_bill_verified || editing ? (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800" data-testid="eway-bill-required-banner">
            Eway Bill required (declared value &gt; ₹50,000). Pickup will be blocked until added.
          </div>
          <Input
            value={val}
            onChange={(e) => setVal(e.target.value.replace(/\D/g, "").slice(0, 12))}
            placeholder="12-digit Eway Bill"
            data-testid="eway-bill-input"
          />
          <Button
            onClick={save}
            disabled={busy || val.length !== 12}
            data-testid="eway-bill-save"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Save Eway Bill
          </Button>
          {editing && (
            <button onClick={() => { setEditing(false); setVal(""); }} className="text-xs text-slate-500 hover:text-slate-800">
              Cancel
            </button>
          )}
        </>
      ) : (
        <>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-800 flex items-center justify-between" data-testid="eway-bill-verified-banner">
            <span>Eway Bill verified ✓</span>
            <span className="font-mono">{masked}</span>
          </div>
          <Button
            variant="outline"
            onClick={() => setEditing(true)}
            data-testid="eway-bill-update"
            className="w-full"
          >
            Update
          </Button>
        </>
      )}
    </div>
  );
}
